import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { RowsSkeleton } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Field, FormActions, Notice, PageHead, TextField, useToast } from "../ui/kit";
import { PageState } from "../ui/pieces";
import { countryName, currencyName, languageName, regionFor, type Country } from "./organizations";
import styles from "./orgs.module.css";

type Reply = {
  saved: boolean;
  organizationId?: number;
  name?: string;
  messageKey?: string;
  fields?: { field: string; messageKey: string }[];
};

const fieldOrder = ["legalName", "commercialName", "country", "timezone", "currency", "locale"];

/**
 * `empresa-nueva` · Empresas › ← Nueva empresa (§5.2, D17): razón social y nombre comercial; país, zona horaria, moneda
 * e idioma del catálogo de países (B5). Al elegir el país se ponen su primera zona, su moneda y su idioma, que se pueden
 * cambiar; por defecto Perú. Al crear: toast y su Resumen, con Chat ya encendido (no se apaga) y el resto de Módulos
 * para decidir antes de «Agregar usuario» (el primero queda como propietario).
 */
export function OrganizationNewPage() {
  const { L } = useTexts();
  const navigate = useNavigate();
  const toast = useToast();
  useTitle(L("ADM_ORGS_NEW"));
  const catalog = useAiData<Country[]>("/api/admin/catalog/countries");
  const [legalName, setLegalName] = useState("");
  const [commercialName, setCommercialName] = useState("");
  const [region, setRegion] = useState<ReturnType<typeof regionFor> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const back = { href: "/Organizations/Index", label: L("ADM_AREA_ORGS") };

  const head = (actions: boolean) => (
    <PageHead title={L("ADM_ORGS_NEW")} back={back}>
      {actions ? (
        <span className={ui.desktopOnly}>
          <a className="tm-btn secondary" href="/Organizations/Index">
            {L("ACTION_CANCEL")}
          </a>
          <button type="submit" form="org-new" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
            {L("ADM_ORGS_CREATE")}
          </button>
        </span>
      ) : null}
    </PageHead>
  );
  if (catalog.state === "failed" || catalog.state === "missing")
    return (
      <div className={styles.formPage}>
        <PageState
          head={head(false)}
          title={L("ADM_UNAVAILABLE")}
          line={L("ADM_UNAVAILABLE_LINE")}
          action={
            <button type="button" className="tm-btn secondary" onClick={catalog.retry}>
              {L("ADM_RETRY")}
            </button>
          }
        />
      </div>
    );
  const countries = catalog.data ?? [];
  // Por defecto, el primero del catálogo (Perú) con sus valores; después, lo que la persona eligió.
  const current = region ?? regionFor(countries[0]);
  const country = countries.find((item) => item.code === current.countryCode);
  const currencies = [...new Set(countries.map((item) => item.currencyCode))];
  const locales = [...new Set(countries.map((item) => item.locale))];

  const focusFirst = (found: Record<string, string>) => {
    const first = fieldOrder.find((field) => found[field]);
    if (first)
      requestAnimationFrame(() =>
        form.current?.querySelector<HTMLElement>(`[data-field="${first}"] :is(input,button)`)?.focus(),
      );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found: Record<string, string> = {};
    if (!legalName.trim()) found.legalName = L("FEEDBACK_ORG_NAME_REQUIRED");
    setErrors(found);
    setError(null);
    if (Object.keys(found).length > 0) return focusFirst(found);
    setBusy(true);
    try {
      const reply = await postJson<Reply>("/api/admin/organizations", {
        legalName: legalName.trim(),
        commercialName: commercialName.trim() || null,
        countryCode: current.countryCode,
        timezone: current.timezone,
        currencyCode: current.currencyCode,
        locale: current.locale,
      });
      if (reply.body?.saved && reply.body.organizationId) {
        // D17: a su Resumen, no directo a Agregar usuario — ahí se ve y se enciende Módulos antes del primer usuario.
        toast.show(formatText(L("ADM_ORGS_CREATED"), commercialName.trim() || legalName.trim()));
        navigate(`/Organizations/Detail?id=${reply.body.organizationId}`);
        return;
      }
      // Un error del servidor nunca borra lo escrito: el campo que falla o un aviso arriba.
      const fields = Object.fromEntries((reply.body?.fields ?? []).map((item) => [item.field, L(item.messageKey)]));
      setErrors(fields);
      if (Object.keys(fields).length > 0) focusFirst(fields);
      else setError(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
    } catch {
      setError(L("ADMIN_ERR_GENERIC"));
    } finally {
      setBusy(false);
    }
  };

  const menuIcons = {
    chevron: <ChatIcon name="chevron-down" />,
    check: <ChatIcon name="check" />,
    searchIcon: <ChatIcon name="search" />,
    clearIcon: <ChatIcon name="x" />,
    searchLabel: L("CFG1_SEARCH"),
    clearSearchLabel: L("CFG1_R1_CLEAR_SEARCH"),
  };
  return (
    <div className={styles.formPage}>
      {head(true)}
      {catalog.state === "loading" ? (
        <RowsSkeleton rows={3} table />
      ) : (
        <form id="org-new" ref={form} className={`tm-form ${ui.formGrid}`} noValidate onSubmit={(e) => void submit(e)}>
          {error ? (
            <div className={ui.formSummary}>
              <Notice tone="error">{error}</Notice>
            </div>
          ) : null}
          <TextField
            id="org-legal"
            field="legalName"
            label={L("ORG_LEGAL_NAME")}
            value={legalName}
            error={errors.legalName}
            maxLength={200}
            placeholder={L("ADM_ORGS_LEGAL_EXAMPLE")}
            onChange={(value) => {
              setLegalName(value);
              // E81: el error se va en cuanto el valor es válido.
              if (value.trim()) setErrors((now) => ({ ...now, legalName: "" }));
            }}
          />
          <TextField
            id="org-commercial"
            field="commercialName"
            label={L("COMPANY_COMMERCIAL_NAME")}
            optional={L("CFG1_R1_OPTIONAL")}
            value={commercialName}
            error={errors.commercialName}
            maxLength={200}
            placeholder={L("ADM_ORGS_COMMERCIAL_EXAMPLE")}
            onChange={setCommercialName}
          />
          <Field
            id="org-country"
            field="country"
            label={L("ORG_COUNTRY")}
            error={errors.country}
            help={L("ADM_ORGS_COUNTRY_HELP")}
          >
            <ChoiceMenu
              id="org-country"
              label={L("ORG_COUNTRY")}
              describedBy="org-country-help"
              value={current.countryCode}
              options={countries.map((item) => ({ value: item.code, label: countryName(L, item.code, item.name) }))}
              // D17: el país nuevo vuelve a poner su zona, su moneda y su idioma.
              onChange={(code) => setRegion(regionFor(countries.find((item) => item.code === code)))}
              {...menuIcons}
            />
          </Field>
          <Field
            id="org-timezone"
            field="timezone"
            label={L("ORG_TIMEZONE")}
            error={errors.timezone}
            help={L("ADM_ORGS_TZ_HELP")}
          >
            <ChoiceMenu
              id="org-timezone"
              label={L("ORG_TIMEZONE")}
              describedBy="org-timezone-help"
              value={current.timezone}
              options={(country?.timezones ?? []).map((zone) => ({ value: zone.id, label: zone.label }))}
              onChange={(timezone) => setRegion({ ...current, timezone })}
              {...menuIcons}
            />
          </Field>
          <Field id="org-currency" field="currency" label={L("ORG_CURRENCY")} error={errors.currency}>
            <ChoiceMenu
              id="org-currency"
              label={L("ORG_CURRENCY")}
              value={current.currencyCode}
              options={currencies.map((code) => ({ value: code, label: currencyName(L, code) }))}
              onChange={(currencyCode) => setRegion({ ...current, currencyCode })}
              {...menuIcons}
            />
          </Field>
          <Field id="org-locale" field="locale" label={L("ORG_LANGUAGE")} error={errors.locale}>
            <ChoiceMenu
              id="org-locale"
              label={L("ORG_LANGUAGE")}
              value={current.locale}
              options={locales.map((code) => ({ value: code, label: languageName(L, code) }))}
              onChange={(locale) => setRegion({ ...current, locale })}
              {...menuIcons}
            />
          </Field>
          <p className={`${ui.full} ${styles.formNote}`}>{L("ADM_ORGS_NEW_NOTE")}</p>
          <div className={ui.mobileOnly}>
            <FormActions>
              <a className="tm-btn secondary" href="/Organizations/Index">
                {L("ACTION_CANCEL")}
              </a>
              <button type="submit" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
                {L("ADM_ORGS_CREATE")}
              </button>
            </FormActions>
          </div>
        </form>
      )}
    </div>
  );
}
